import {useTranslation} from 'react-i18next';
import './templates.professional-caregiver.scss';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {EntityTemplatesRepository} from '../../../../../../repositories/entity-templates';
import {EntityTemplate} from '../../../../../../backend/responses/entity-template';
import {Box, Stack, Typography} from '@mui/material';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {ScreenPaths} from '../../../../../../enums';
import {translations} from '../../../../../../i18n/translations';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {useAuth} from '../../../../../../contexts/auth/context';
import TemplatesTable from './templates-table';
import {SettingsTemplatesModal} from './settings-templates-modal';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';

export default function TemplatesProfessionalCaregiverOfficeScreen() {
  let {entityId}: {entityId?: string} = useParams();
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const snackBarContext = useSnackBar();
  const {state: authState, hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Settings_Templates);
  const [templates, setTemplates] = useState<EntityTemplate[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [templateToDelete, setTemplateToDelete] = useState<EntityTemplate>();
  const [duplicatedTemplate, setDuplicateTemplate] = useState<EntityTemplate>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const getTemplatesData = async (id: string) => {
      try {
        const data = await EntityTemplatesRepository.instance.getTemplates(id);
        setTemplates(data);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
      }
    };
    if (entityId) {
      getTemplatesData(entityId);
    }
  }, [entityId]);

  const handleDuplicateTemplate = async (template: EntityTemplate) => {
    setIsSubmitting(true);
    if (!entityId) return;
    try {
      const duplicated = await EntityTemplatesRepository.instance.duplicateTemplate(
        entityId,
        template.id
      );
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.office.professional_caregiver.settings.templates.snackbar
            .duplicate_success_message,
          {
            name: template.name,
          }
        ),
      });
      setDuplicateTemplate(duplicated);
      const index = templates?.findIndex((item) => item.id === template.id) || 0;
      setTemplates((prev = []) => {
        return [...prev.slice(0, index + 1), duplicated, ...prev.slice(index + 1)];
      });
      setIsSubmitting(false);
    } catch (e) {
      console.error(e);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.templates.snackbar
            .duplicate_error_message,
          {
            name: template.name,
          }
        ),
      });
      setIsSubmitting(false);
    }
  };

  const handleDeleteTemplates = async () => {
    setIsSubmitting(true);
    if (!templateToDelete || !entityId) return;
    try {
      await EntityTemplatesRepository.instance.deleteTemplate(entityId, templateToDelete.id);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.office.professional_caregiver.settings.templates.snackbar
            .delete_success_message,
          {
            name: templateToDelete.name,
          }
        ),
      });
      setTemplates((prev) => prev?.filter((template) => template.id !== templateToDelete.id));
      setIsSubmitting(false);
      setTemplateToDelete(undefined);
    } catch (e) {
      console.error(e);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.templates.snackbar
            .delete_error_message,
          {
            name: templateToDelete.name,
          }
        ),
      });
      setIsSubmitting(false);
      setTemplateToDelete(undefined);
    }
  };

  return (
    <Box className="settings-menu-wrapper">
      <GenericHeader
        titleCrumbs={[
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .settings
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`,
          },
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .templates
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Typography className="settings-menu-header-title" noWrap>
            {t(
              translations.office.professional_caregiver.settings.generic_header.subtitle.templates[
                canWrite ? 'write' : 'read'
              ]
            )}
          </Typography>
        }
      />

      <div>
        {!isLoading && templates?.length === 0 ? (
          <Stack className="care-table-empty" justifyContent="center" alignItems="center">
            <h3 className="care-table-empty-title">
              {t(translations.office.professional_caregiver.settings.templates.empty)}
            </h3>
          </Stack>
        ) : (
          <TemplatesTable
            data={templates}
            isLoading={isLoading}
            canWrite={canWrite}
            duplicated={duplicatedTemplate}
            onDelete={(template) => setTemplateToDelete(template)}
            onDuplicate={(template) => handleDuplicateTemplate(template)}
            onEdit={(template) => {
              history.push(`${location.pathname}/${template.id}`, {
                templateId: template.id,
              });
            }}
          />
        )}
        {templateToDelete && (
          <SettingsTemplatesModal
            open
            submitting={isSubmitting}
            onClose={() => setTemplateToDelete(undefined)}
            onDelete={handleDeleteTemplates}
            template={templateToDelete}
          />
        )}
      </div>
    </Box>
  );
}
